import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { card, input, label, btnPrimary, btnGhost, h1 } from "./ui";
import { ShieldCheck, Mail, KeyRound, Loader2, Eye, EyeOff, Users, UserPlus, Trash2, Video, Link2Off, Sparkles } from "lucide-react";

export default function Security() {
  const { user, checkAuth } = useAuth();
  const isGoogle = user?.auth_provider === "google";

  return (
    <div data-testid="security-page" className="max-w-2xl">
      <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Conta</p>
      <h1 className={h1}>Segurança do painel</h1>
      <p className="font-body text-sm text-[#7A8A7A] mt-2">Altere o e-mail e a senha de acesso ao seu painel.</p>

      {isGoogle ? (
        <div className={`${card} p-6 mt-8`} data-testid="security-google-note">
          <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]"><ShieldCheck className="w-5 h-5 text-[#5B6E5B]" /> Login pelo Google</p>
          <p className="font-body text-sm text-[#7A8A7A] mt-3">Sua conta entra pelo Google, então o e-mail e a senha são gerenciados pela sua conta Google — não é preciso trocar por aqui.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <ChangeEmailCard currentEmail={user?.email} onDone={checkAuth} />
          <ChangePasswordCard />
        </div>
      )}

      <GoogleMeetCard />

      <AiModelCard />

      <UsersManager currentUserId={user?.user_id} />
    </div>
  );
}

function Field({ id, labelTxt, type = "text", value, onChange, placeholder, toggleable }) {
  const [show, setShow] = useState(false);
  const realType = toggleable ? (show ? "text" : "password") : type;
  return (
    <div>
      <label className={label}>{labelTxt}</label>
      <div className="relative">
        <input type={realType} className={input} value={value} onChange={onChange} placeholder={placeholder} data-testid={id} />
        {toggleable && (
          <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8A7A] hover:text-[#2C332A]" data-testid={`${id}-toggle`}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function ChangeEmailCard({ currentEmail, onDone }) {
  const [password, setPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !password) return toast.error("Preencha o novo e-mail e a senha atual.");
    setSaving(true);
    try {
      await api.post("/auth/change-email", { password, new_email: newEmail.trim() });
      toast.success("E-mail de acesso atualizado! 🌿");
      setPassword(""); setNewEmail("");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className={`${card} p-6`} data-testid="change-email-form">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]"><Mail className="w-5 h-5 text-[#5B6E5B]" /> Trocar e-mail de acesso</p>
      <p className="font-body text-sm text-[#7A8A7A] mt-1">E-mail atual: <strong className="text-[#4A5D4E]">{currentEmail}</strong></p>
      <div className="space-y-4 mt-5">
        <Field id="new-email-input" labelTxt="Novo e-mail" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="seunovo@email.com" />
        <Field id="email-current-password" labelTxt="Sua senha atual (para confirmar)" toggleable value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
      </div>
      <div className="flex justify-end mt-5">
        <button className={btnPrimary} disabled={saving} data-testid="change-email-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Salvar novo e-mail
        </button>
      </div>
    </form>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("A nova senha deve ter pelo menos 8 caracteres.");
    if (next !== confirm) return toast.error("A confirmação não confere com a nova senha.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Senha atualizada com sucesso! 🔒");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className={`${card} p-6`} data-testid="change-password-form">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]"><KeyRound className="w-5 h-5 text-[#5B6E5B]" /> Trocar senha</p>
      <div className="space-y-4 mt-5">
        <Field id="current-password" labelTxt="Senha atual" toggleable value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" />
        <Field id="new-password" labelTxt="Nova senha (mín. 8 caracteres)" toggleable value={next} onChange={(e) => setNext(e.target.value)} placeholder="••••••••" />
        <Field id="confirm-password" labelTxt="Confirmar nova senha" toggleable value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
      </div>
      <div className="flex justify-end mt-5">
        <button className={btnPrimary} disabled={saving} data-testid="change-password-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Atualizar senha
        </button>
      </div>
    </form>
  );
}


function UsersManager({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Informe o nome.");
    if (password.length < 8) return toast.error("A senha deve ter pelo menos 8 caracteres.");
    setSaving(true);
    try {
      await api.post("/auth/users", { name: name.trim(), email: email.trim(), password });
      toast.success("Novo acesso criado! 🌿");
      setName(""); setEmail(""); setPassword(""); setShow(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remover o acesso de ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/auth/users/${u.user_id}`);
      toast.success("Acesso removido.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className={`${card} p-6 mt-6`} data-testid="users-manager">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]"><Users className="w-5 h-5 text-[#5B6E5B]" /> Acessos ao painel</p>
      <p className="font-body text-sm text-[#7A8A7A] mt-1">Contas que podem entrar no painel. Todas têm acesso completo (administrador).</p>

      <div className="mt-5 divide-y divide-[#5B6E5B]/10" data-testid="users-list">
        {loading ? (
          <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#5B6E5B]" /></div>
        ) : users.map((u) => (
          <div key={u.user_id} className="py-3 flex items-center justify-between gap-3" data-testid="user-row">
            <div className="min-w-0">
              <p className="font-body text-sm text-[#2C332A]">{u.name}
                {u.user_id === currentUserId && <span className="ml-2 rounded-full bg-[#5B6E5B]/12 text-[#4A5D4E] text-[11px] px-2 py-0.5">você</span>}
                {u.auth_provider === "google" && <span className="ml-2 rounded-full bg-[#F5F0E6] text-[#7A8A7A] text-[11px] px-2 py-0.5">Google</span>}
              </p>
              <p className="font-body text-xs text-[#A8B0A2]">{u.email}</p>
            </div>
            {u.user_id !== currentUserId && (
              <button onClick={() => remove(u)} className="text-[#C87A5E] hover:text-[#B2644D] shrink-0" data-testid="user-delete" title="Remover acesso"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={create} className="mt-6 border-t border-[#5B6E5B]/10 pt-5" data-testid="create-user-form">
        <p className="inline-flex items-center gap-2 font-body text-sm font-medium text-[#4A5D4E]"><UserPlus className="w-4 h-4 text-[#5B6E5B]" /> Criar novo acesso</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className={label}>Nome</label>
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da pessoa" data-testid="new-user-name" />
          </div>
          <div>
            <label className={label}>E-mail de acesso</label>
            <input type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pessoa@email.com" data-testid="new-user-email" />
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Senha (mín. 8 caracteres)</label>
            <div className="relative">
              <input type={show ? "text" : "password"} className={input} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="new-user-password" />
              <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8A7A] hover:text-[#2C332A]" data-testid="new-user-password-toggle">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button className={btnPrimary} disabled={saving} data-testid="create-user-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Criar acesso
          </button>
        </div>
      </form>
    </div>
  );
}

function GoogleMeetCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cid, setCid] = useState("");
  const [csec, setCsec] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [editCreds, setEditCreds] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/google/status");
      setSt(data);
    } catch { setSt(null); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const g = params.get("google");
    if (g === "conectado") toast.success("Conta Google conectada! Agora a Sala pode gerar o link do Meet sozinha.");
    if (g === "erro") toast.error("Não consegui conectar ao Google. Tente novamente.");
    if (g) setParams({}, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/google/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/google/disconnect");
      toast.success("Conta Google desconectada. A Sala volta ao modo manual.");
      await load();
    } catch { toast.error("Não consegui desconectar."); }
    setBusy(false);
  };
  const saveCreds = async (e) => {
    e.preventDefault();
    setSavingCreds(true);
    try {
      await api.post("/google/credentials", { client_id: cid.trim(), client_secret: csec.trim() });
      toast.success("Credenciais salvas! Agora clique em Conectar conta Google.");
      setCid(""); setCsec(""); setEditCreds(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingCreds(false); }
  };
  const removeCreds = async () => {
    setBusy(true);
    try {
      await api.delete("/google/credentials");
      toast.success("Credenciais removidas. O Meet automático foi desativado.");
      await load();
    } catch { toast.error("Não consegui remover as credenciais."); }
    setBusy(false);
  };

  const credsForm = (
    <form onSubmit={saveCreds} className="mt-4 space-y-3" data-testid="google-creds-form">
      <p className="font-body text-xs text-[#7A8A7A] leading-relaxed">
        Cole aqui as credenciais criadas no Google Cloud (console.cloud.google.com → APIs e serviços → Credenciais → ID do cliente OAuth). Elas ficam guardadas com segurança no seu painel.
      </p>
      <div>
        <label className={label}>Client ID</label>
        <input className={input} value={cid} onChange={(e) => setCid(e.target.value)}
          placeholder="xxxx.apps.googleusercontent.com" data-testid="google-client-id" required />
      </div>
      <div>
        <label className={label}>Client Secret</label>
        <input className={input} type="password" value={csec} onChange={(e) => setCsec(e.target.value)}
          placeholder="GOCSPX-..." data-testid="google-client-secret" required />
      </div>
      <button type="submit" disabled={savingCreds} className={`${btnPrimary} inline-flex items-center gap-2`} data-testid="google-save-creds">
        {savingCreds ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Salvar credenciais
      </button>
    </form>
  );

  return (
    <div className={`${card} p-6 mt-8`} data-testid="google-meet-card">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]">
        <Video className="w-5 h-5 text-[#5B6E5B]" /> Google Meet automático
      </p>
      <p className="font-body text-sm text-[#7A8A7A] mt-3 leading-relaxed">
        Conecte sua conta Google uma única vez e a Sala de atendimento passa a gerar o link do Meet
        sozinha para cada sessão — sem precisar criar e colar manualmente.
      </p>
      {st === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-[#5B6E5B] mt-4" />
      ) : st.connected ? (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="font-body text-sm text-[#2C332A]" data-testid="google-connected-as">
            Conectado como <strong>{st.email}</strong>
          </p>
          <button onClick={disconnect} disabled={busy} data-testid="google-disconnect"
            className={`${btnGhost} inline-flex items-center gap-2`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2Off className="w-4 h-4" />} Desconectar
          </button>
        </div>
      ) : (
        <div className="mt-4">
          {st.enabled && (
            <>
              <button onClick={connect} disabled={busy} data-testid="google-connect"
                className={`${btnPrimary} inline-flex items-center gap-2`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Conectar conta Google
              </button>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <p className="font-body text-xs text-[#7A8A7A]" data-testid="google-creds-saved">
                  Credenciais salvas ({st.client_id_hint})
                </p>
                {st.source === "db" && (
                  <>
                    <button onClick={() => setEditCreds((v) => !v)} data-testid="google-edit-creds"
                      className="font-body text-xs underline text-[#5B6E5B] hover:text-[#2C332A]">alterar</button>
                    <button onClick={removeCreds} disabled={busy} data-testid="google-remove-creds"
                      className="font-body text-xs underline text-[#C87A5E] hover:text-[#2C332A]">remover</button>
                  </>
                )}
              </div>
            </>
          )}
          {(!st.enabled || editCreds) && credsForm}
        </div>
      )}
    </div>
  );
}

function AiModelCard() {
  const [st, setSt] = useState(null);
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [ownKey, setOwnKey] = useState("");
  const [showKeyForm, setShowKeyForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/settings");
      setSt(data);
      setSel(`${data.provider}|${data.model}`);
    } catch { setSt(null); }
  };
  useEffect(() => { load(); }, []);

  const save = async (removeKey = false) => {
    const [provider, model] = sel.split("|");
    setSaving(true);
    try {
      const payload = { provider, model };
      if (removeKey) payload.api_key = "";
      else if (ownKey.trim()) payload.api_key = ownKey.trim();
      await api.post("/ai/settings", payload);
      toast.success(removeKey
        ? "Chave própria removida. Voltamos à chave da plataforma."
        : "Modelo de IA atualizado. Vale para o assistente, a IA do paciente e as próximas gerações.");
      setOwnKey("");
      setShowKeyForm(false);
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/ai/test");
      setTestResult({ ok: true, text: `“${data.reply}” — respondeu em ${(data.ms / 1000).toFixed(1)}s` });
    } catch (e) {
      setTestResult({ ok: false, text: formatApiError(e.response?.data?.detail) || "O modelo não respondeu." });
    } finally { setTesting(false); }
  };

  const selOpt = st?.options?.find((o) => `${o.provider}|${o.model}` === sel);
  const dirty = st && (sel !== `${st.provider}|${st.model}` || Boolean(ownKey.trim()));

  return (
    <div className={`${card} p-6 mt-8`} data-testid="ai-model-card">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]">
        <Sparkles className="w-5 h-5 text-[#5B6E5B]" /> Inteligência artificial
      </p>
      <p className="font-body text-sm text-[#7A8A7A] mt-3 leading-relaxed">
        Escolha qual IA o consultório usa (assistente, IA do paciente, leitura de fotos e mensagens).
        Se um modelo falhar, o sistema usa o Claude Sonnet 4.6 automaticamente naquela chamada.
      </p>
      {st === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-[#5B6E5B] mt-4" />
      ) : (
        <div className="mt-4 space-y-4">
          <div>
            <label className={label}>Modelo em uso</label>
            <select className={input} value={sel} onChange={(e) => setSel(e.target.value)} data-testid="ai-model-select">
              {st.options.map((o) => (
                <option key={`${o.provider}|${o.model}`} value={`${o.provider}|${o.model}`}>
                  {o.label} — {o.note}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => save()} disabled={saving || !dirty} data-testid="ai-model-save"
              className={`${btnPrimary} inline-flex items-center gap-2 disabled:opacity-50`}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar modelo
            </button>
            <button onClick={test} disabled={testing} data-testid="ai-model-test"
              className={`${btnGhost} inline-flex items-center gap-2`}>
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Testar IA atual
            </button>
          </div>
          {testResult && (
            <p className={`font-body text-sm ${testResult.ok ? "text-[#4A5D4E]" : "text-[#C87A5E]"}`} data-testid="ai-model-test-result">
              {testResult.text}
            </p>
          )}
          <div className="border-t border-[#5B6E5B]/10 pt-4">
            {st.has_own_key ? (
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-body text-xs text-[#4A5D4E]" data-testid="ai-own-key-note">
                  Usando sua chave própria de API ({st.key_hint})
                </p>
                <button onClick={() => save(true)} disabled={saving} data-testid="ai-remove-key"
                  className="font-body text-xs underline text-[#C87A5E] hover:text-[#2C332A]">remover chave</button>
              </div>
            ) : (
              <>
                <button onClick={() => setShowKeyForm((v) => !v)} data-testid="ai-own-key-toggle"
                  className="font-body text-xs underline text-[#5B6E5B] hover:text-[#2C332A]">
                  Usar minha própria chave de API (opcional)
                </button>
                {showKeyForm && (
                  <div className="mt-3">
                    <label className={label}>Sua chave de API</label>
                    <input type="password" className={input} value={ownKey} onChange={(e) => setOwnKey(e.target.value)}
                      placeholder="sk-..." data-testid="ai-own-key-input" autoComplete="off" />
                    <p className="font-body text-xs text-[#7A8A7A] mt-1.5 leading-relaxed">
                      A chave precisa ser do mesmo provedor do modelo escolhido (OpenAI para GPT, Anthropic para Claude,
                      Google para Gemini). Vazia = usa a chave da plataforma, já incluída. A transcrição de áudio sempre
                      usa a chave da plataforma. Depois de salvar, clique em "Testar IA atual" para confirmar.
                    </p>
                  </div>
                )}
              </>
            )}
            {!st.has_own_key && !showKeyForm && (
              <p className="font-body text-xs text-[#A8B0A2] mt-2">Sem chave própria, o sistema usa a chave da plataforma (já incluída).</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

